import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { genererCanauxRuban, MATCAP_GAIN, SATIN_BOX, CHROME_BOX } from './lib/matcap.js';
import { vdjColor, vdjLoop, VdjPost, VdjComfort, VDJ_PRESETS, vdjBeatBreath, vdjBarChroma } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. La lumière est dans la matcap.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'ribbons',
  family: 'object',
  danceability: 0.62,
  requiresTier: 0,
  energyLevel: 0.52,
  bpmMin: 85,
  bpmMax: 125,
  movement: 0.55,
  tonality: 0.80,
  enabled: true
};

// Ribbons : six à dix rubans larges et brillants qui flottent et ondulent
// dans le noir, se tordent sur eux-mêmes et reflètent des boîtes à lumière.
// Deep house, nu-disco, R&B, soul électronique, pop. LOOK VDJ, preset LOUNGE
// (VDJ_PRESETS.lounge) : coupes sur une phrase sur deux seulement, fondus
// d'une mesure permis, boucles de 16 / 32 / 64 temps, le temps comme une
// respiration (+8 % de luminance au plus), séparation RGB sur la mesure
// seulement, caméra sans à-coup, drop sans flash (+25 % d'intensité sur une
// mesure, puis la variante la plus riche pendant 8 mesures), luminance
// moyenne sous 0,35.
//
// LA GÉOMÉTRIE : UNE bande de SEG x 2 sommets (position.x = s dans [0, 1],
// position.y = côté -1 / +1), partagée par tous les rubans d'un seul
// InstancedMesh. TOUT le reste est dans le vertex shader, à partir
// d'attributs d'instance statiques - aucune position calculée côté CPU :
//   - la ligne centrale, dans le repère local du ruban : x avance de L sur
//     la longueur, et chaque axe porte trois sinus (fréquence le long du
//     ruban, pulsation ENTIÈRE en tours par 32 temps : la boucle retombe
//     exactement sur 32 temps), phases tirées d'une graine ;
//   - LE REPÈRE par différences finies : T = c(s + e) - c(s - e) ; N = la
//     verticale locale transportée par la plus courte rotation qui amène
//     l'axe x sur T ; B = T x N. AUCUN retournement possible : les
//     amplitudes sont bornées pour que dx/ds reste positif, et cette
//     rotation n'est singulière que pour T = -x (vérifié par
//     test-ribbons.mjs sur la copie JS de la courbe).
//     Un repère de Frenet (dérivée seconde) se retourne à chaque point
//     d'inflexion : c'est précisément ce qu'il fallait éviter ;
//   - la TORSION : la bande tourne autour de la ligne centrale (angle =
//     s x torsion + temps) - c'est elle qui fait courir les reflets ;
//   - puis une orientation (quaternion) et un centre par ruban.
// Deux jeux de fréquences par ruban (A et B) : sur une phrase, B reçoit un
// nouvel ensemble et uSetMix fond A vers B en une mesure - les rubans se
// réorganisent sans saut ; à la fin B est recopié dans A (un envoi, une fois).
//
// LE MATÉRIAU : la matcap de Totem en canaux (lib/matcap.js) - SATIN et
// CHROME dans UNE texture RGBA, lue une fois par fragment ; la teinte du
// ruban est appliquée dans le shader. Double face : le dos est plus sombre,
// d'une teinte voisine. Les plus éloignés sont plus sombres et moins
// contrastés. Le seul élément qui fleurit vraiment : un reflet blanc court
// qui parcourt chaque ruban d'un bout à l'autre en une mesure.

const TIER = {
  0: { count: 6, seg: 200 },
  1: { count: 8, seg: 400 },
  2: { count: 8, seg: 400 }
};
const EXTRA = 2;                         // les deux rubans du drop
export const RIBBON_L = 5.5;             // longueur locale (x)
export const AMP_X = [0.16, 0.06, 0.02];
export const AMP_YZ = [0.66, 0.20, 0.05];
export const FREQ_RANGE = [[0.35, 0.7], [0.9, 1.3], [1.6, 2.2]];
const OMEGAS = [[1, -1], [2, -1, 1], [2, -2, 3]];
const LOOP_BEATS = 32, TWIST_BEATS = 16, ORBIT_BEATS = 64, BREATH_BEATS = 32;
const WIDTH = 0.58;
const BEAT_WIDTH = 0.06;                 // le temps : la largeur respire de 6 %
const SAFE_FLOOR = 0.45;
const STREAK_GAIN = 1.3;
const DROP_BARS = 8, DROP_TWIST = 1.6, REVEAL_BARS = 2;
const PAUSE_RATE = 0.05;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const LOUNGE = VDJ_PRESETS.lounge;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  try { return new URLSearchParams(location.search).get(name); } catch (e) { return null; }
}
function rng(seed) { let s = seed >>> 0; return () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; }; }

// ------------------------------------------------- la courbe (copie JS exacte)

// les phases, tirées de la graine : mêmes constantes que le shader
const PH_OFF = { x: [0.0, 0.381966, 0.763932], y: [0.145, 0.527, 0.909], z: [0.291, 0.673, 0.055] };
function fract(v) { return v - Math.floor(v); }
// set = { fx: [3], fy: [3], fz: [3], w: [3] }, t = 2 pi x temps / 32
export function courbeLocale(s, set, seed, t, ripple, out) {
  const o = out || [0, 0, 0];
  let x = RIBBON_L * (s - 0.5), y = 0, z = 0;
  for (let k = 0; k < 3; k++) {
    const extra = k === 2 ? ripple : 0;
    x += AMP_X[k] * Math.sin(2 * Math.PI * s * set.fx[k] + 2 * Math.PI * fract(seed * 0.618034 + PH_OFF.x[k]) + set.w[k] * t + extra);
    y += AMP_YZ[k] * Math.sin(2 * Math.PI * s * set.fy[k] + 2 * Math.PI * fract(seed * 0.618034 + PH_OFF.y[k]) + set.w[k] * t + extra);
    z += AMP_YZ[k] * Math.sin(2 * Math.PI * s * set.fz[k] + 2 * Math.PI * fract(seed * 0.618034 + PH_OFF.z[k]) + set.w[k] * t + extra);
  }
  o[0] = x; o[1] = y; o[2] = z;
  return o;
}
// le repère (T, N, B) comme le shader : différences finies, N = verticale - (verticale.T) T
export function repere(s, set, seed, t, ripple, e) {
  const a = courbeLocale(s - e, set, seed, t, ripple), b = courbeLocale(s + e, set, seed, t, ripple);
  const T = new THREE.Vector3(b[0] - a[0], b[1] - a[1], b[2] - a[2]).normalize();
  // N : la verticale locale TRANSPORTÉE par la plus courte rotation qui amène
  // l'axe x du ruban sur T (Rodrigues, k = x x T, c = T.x). Singulière
  // seulement pour T = -x, impossible puisque x avance toujours. La première
  // version (verticale moins sa composante sur T) ne se retournait pas mais
  // tournait de 22 degrés sur 1 % du ruban quand T frôlait la verticale.
  const ci = 1 / (1 + T.x);
  const N = new THREE.Vector3(-T.y, T.x + T.z * T.z * ci, -T.y * T.z * ci).normalize();
  const B = new THREE.Vector3().crossVectors(T, N);
  return { T, N, B };
}
export function tirerJeu(r) {
  const f = (k) => FREQ_RANGE[k][0] + (FREQ_RANGE[k][1] - FREQ_RANGE[k][0]) * r();
  const w = OMEGAS.map((list) => list[Math.floor(r() * list.length)]);
  return { fx: [f(0), f(1), f(2)], fy: [f(0), f(1), f(2)], fz: [f(0), f(1), f(2)], w, seed: Math.floor(r() * 997) + r() };
}

// ------------------------------------------------------------------ shaders

const VERTEX = `
  precision highp float;
  // SEIZE ATTRIBUTS AU PLUS (la limite du GPU, Pi compris) : position,
  // normal et uv que three déclare, instanceMatrix (4), et nos 9. Les deux
  // graines sont donc rangées dans iCenter.w et iMisc.w, l'indice du ruban
  // vient de gl_InstanceID - avec un attribut de plus, le programme ne se lie
  // pas (« Too many attributes »).
  attribute vec4 iCenter;      // centre, graine du jeu A
  attribute vec4 iOrient;
  attribute vec4 iMisc;        // largeur, tours de torsion, rubans du drop (0/1), graine du jeu B
  attribute vec4 iA0; attribute vec4 iA1; attribute vec4 iA2;
  attribute vec4 iB0; attribute vec4 iB1; attribute vec4 iB2;
  uniform float uT;            // 2 pi x (temps modulo 32) / 32
  uniform float uRipple;       // la vague rapide (bands.mid), intégrée
  uniform float uSetMix;
  uniform float uTwistT;       // 2 pi x (temps modulo 16) / 16
  uniform float uTwistGain;
  uniform float uWidth;
  uniform float uReveal;
  uniform float uEps;
  uniform vec3 uTints[12];
  uniform vec3 uBacks[12];
  varying vec3 vNormal;
  varying float vS;
  varying float vSide;
  varying vec3 vTint;
  varying vec3 vBack;
  varying float vDepth;
  varying vec4 vClip;
  varying float vStreakOff;

  const vec3 AX = vec3(${AMP_X.join(', ')});
  const vec3 AYZ = vec3(${AMP_YZ.join(', ')});
  vec3 courbe(float s, vec4 X, vec4 Y, vec4 Z, float seed) {
    vec3 w = vec3(X.w, Y.w, Z.w);
    vec3 ph = vec3(0.0, 0.0, uRipple);
    vec3 px = 6.2831853 * fract(seed * 0.618034 + vec3(${PH_OFF.x.join(', ')}));
    vec3 py = 6.2831853 * fract(seed * 0.618034 + vec3(${PH_OFF.y.join(', ')}));
    vec3 pz = 6.2831853 * fract(seed * 0.618034 + vec3(${PH_OFF.z.join(', ')}));
    float x = ${RIBBON_L.toFixed(2)} * (s - 0.5) + dot(AX, sin(6.2831853 * s * X.xyz + px + w * uT + ph));
    float y = dot(AYZ, sin(6.2831853 * s * Y.xyz + py + w * uT + ph));
    float z = dot(AYZ, sin(6.2831853 * s * Z.xyz + pz + w * uT + ph));
    return vec3(x, y, z);
  }
  vec3 courbeMix(float s) {
    vec3 a = courbe(s, iA0, iA1, iA2, iCenter.w);
    if (uSetMix <= 0.0) return a;
    return mix(a, courbe(s, iB0, iB1, iB2, iMisc.w), uSetMix);
  }
  vec3 tourner(vec4 q, vec3 v) { return v + 2.0 * cross(q.xyz, cross(q.xyz, v) + q.w * v); }

  void main() {
    float s = position.x, side = position.y;
    // les rubans du drop se DÉROULENT : la longueur visible va de 0 à 1
    float reveal = mix(1.0, uReveal, iMisc.z);
    float se = s * reveal;
    vec3 c = courbeMix(se);
    vec3 T = normalize(courbeMix(se + uEps) - courbeMix(se - uEps));
    // la verticale TRANSPORTÉE par la plus courte rotation x -> T (voir repere())
    float ci = 1.0 / (1.0 + T.x);
    vec3 N = normalize(vec3(-T.y, T.x + T.z * T.z * ci, -T.y * T.z * ci));
    vec3 B = cross(T, N);
    float th = 6.2831853 * s * iMisc.y * uTwistGain + uTwistT + iCenter.w;
    vec3 dir = N * cos(th) + B * sin(th);
    vec3 nrm = B * cos(th) - N * sin(th);
    float w = iMisc.x * uWidth * pow(max(sin(3.14159265 * s), 0.0), 0.7) * smoothstep(0.0, 0.15, reveal);
    vec3 p = iCenter.xyz + tourner(iOrient, c + dir * (0.5 * w * side));
    vec4 mv = modelViewMatrix * vec4(p, 1.0);
    vNormal = normalize(normalMatrix * tourner(iOrient, nrm));
    vS = s; vSide = side; vDepth = -mv.z;
    int idx = gl_InstanceID;
    vTint = uTints[idx]; vBack = uBacks[idx];
    vStreakOff = fract(iCenter.w * 0.37) * 0.12 - 0.06;
    vClip = projectionMatrix * mv;
    gl_Position = vClip;
  }
`;

export const FRAGMENT = `
  precision mediump float;
  uniform sampler2D uMat;
  uniform float uMatMix;       // 0 satin, 1 chrome
  uniform float uBarPos;
  uniform float uStreak;
  uniform float uLum;
  uniform vec2 uFar;
  uniform float uSafeAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec3 vNormal;
  varying float vS;
  varying float vSide;
  varying vec3 vTint;
  varying vec3 vBack;
  varying float vDepth;
  varying vec4 vClip;
  varying float vStreakOff;
  float safeAtt(vec2 ndc) {
    vec2 s = vec2(ndc.x * uSafeAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
  void main() {
    vec3 n = normalize(vNormal);
    bool front = gl_FrontFacing;
    if (!front) n = -n;
    if (n.z < 0.0) n = normalize(vec3(n.xy, 0.0) + vec3(0.0, 0.0, 0.02));
    vec4 m = texture2D(uMat, n.xy * 0.49 + 0.5);
    float body = mix(m.r * m.r, m.b * m.b, uMatMix) * ${MATCAP_GAIN.toFixed(2)};
    float box = mix(m.g * ${SATIN_BOX.toFixed(2)}, m.a * ${CHROME_BOX.toFixed(2)}, uMatMix);
    vec3 tint = front ? vTint : vBack;
    float rim = pow(1.0 - n.z, 4.0) * 0.45;
    vec3 col = tint * (body + rim) + vec3(box) * (front ? 1.0 : 0.55);
    // le reflet qui court : court, blanc, au-dessus du seuil de bloom
    float d = vS - uBarPos - vStreakOff;
    col += vec3(${STREAK_GAIN.toFixed(2)}) * exp(-d * d / 0.0009) * (1.0 - 0.5 * vSide * vSide) * uStreak;
    // la profondeur : les plus lointains plus sombres, moins contrastés
    float f = smoothstep(uFar.x, uFar.y, vDepth);
    col = mix(col, tint * 0.05, 0.35 * f) * (1.0 - 0.45 * f);
    gl_FragColor = vec4(col * uLum * safeAtt(vClip.xy / vClip.w), 1.0);
  }
`;

// module-level caches: on the Pi a material recompiles on every appearance otherwise
const GEOMETRIES = new Map();
let MATERIAL = null, MATCAP_TEX = null;
function bande(seg) {
  const key = seg;
  if (GEOMETRIES.has(key)) return GEOMETRIES.get(key);
  const pos = new Float32Array(seg * 2 * 3), idx = [];
  for (let i = 0; i < seg; i++) {
    const s = i / (seg - 1);
    pos[(i * 2) * 3] = s; pos[(i * 2) * 3 + 1] = -1;
    pos[(i * 2 + 1) * 3] = s; pos[(i * 2 + 1) * 3 + 1] = 1;
    if (i < seg - 1) { const a = i * 2; idx.push(a, a + 2, a + 1, a + 1, a + 2, a + 3); }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setIndex(idx);
  GEOMETRIES.set(key, g);
  return g;
}
function matcap() {
  if (MATCAP_TEX) return MATCAP_TEX;
  const m = genererCanauxRuban(256);
  const t = new THREE.DataTexture(m.data, m.size, m.size, THREE.RGBAFormat, THREE.UnsignedByteType);
  t.colorSpace = THREE.NoColorSpace; t.minFilter = THREE.LinearFilter; t.magFilter = THREE.LinearFilter;
  t.generateMipmaps = false; t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping; t.needsUpdate = true;
  MATCAP_TEX = t;
  return t;
}
function material() {
  if (MATERIAL) return MATERIAL;
  MATERIAL = new THREE.ShaderMaterial({
    uniforms: {
      uT: { value: 0 }, uRipple: { value: 0 }, uSetMix: { value: 0 }, uTwistT: { value: 0 }, uTwistGain: { value: 1 },
      uWidth: { value: 1 }, uReveal: { value: 0 }, uEps: { value: 0.003 },
      uTints: { value: Array.from({ length: 12 }, () => new THREE.Color(1, 1, 1)) },
      uBacks: { value: Array.from({ length: 12 }, () => new THREE.Color(0.4, 0.4, 0.4)) },
      uMat: { value: null }, uMatMix: { value: 0 }, uBarPos: { value: -1 }, uStreak: { value: 1 }, uLum: { value: 1 },
      uFar: { value: new THREE.Vector2(7.0, 12.0) },
      uSafeAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    },
    vertexShader: VERTEX, fragmentShader: FRAGMENT, side: THREE.DoubleSide
  });
  return MATERIAL;
}

// ------------------------------------------------------------- le visualiseur

export class RibbonsVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = TIER[o.qualityTier] ? o.qualityTier : 1;
    this._layout = TIER[this._tier];
    const cnt = parseInt(urlParam('ribbonscount'), 10);
    this._count = Number.isFinite(cnt) ? Math.max(1, Math.min(10, cnt)) : this._layout.count;
    const fm = urlParam('ribbonsmat');
    this._forcedMat = fm === 'satin' ? 0 : fm === 'chrome' ? 1 : null;
    this._probe = urlParam('ribbonsprobe') === '1';
    this._post = new VdjPost({ bloomStrength: 0.5, bloomThreshold: 0.78, grain: 0.015, vignette: 0.45 });
    this._comfort = new VdjComfort(LOUNGE.comfort);
  }

  get postProcessing() { return this._post.value; }

  init(scene, camera) {
    this._scene = scene;
    const n = this._count + EXTRA;
    this._n = n;
    const geo = bande(this._layout.seg).clone();      // the strip is shared; the clone only carries this instance set's attributes
    this._geo = geo;
    const r = rng(4242);
    const attrs = {};
    for (const name of ['iCenter', 'iOrient', 'iMisc', 'iA0', 'iA1', 'iA2', 'iB0', 'iB1', 'iB2']) attrs[name] = new THREE.InstancedBufferAttribute(new Float32Array(n * 4), 4);
    const q = new THREE.Quaternion(), e = new THREE.Euler();
    this._sets = [];
    for (let i = 0; i < n; i++) {
      const extra = i >= this._count;
      // les centres : répartis autour de l'axe du groupe, en profondeur
      const a = (i + 0.5) / n * Math.PI * 2 + (r() - 0.5) * 0.6;
      // assez loin de l'axe pour que la pochette (DOM, au centre) n'en cache
      // pas la plupart, pas trop pour que la révolution ne les amène pas
      // contre la caméra (le plus proche reste à ~2,7 unités)
      const rad = 1.4 + 1.6 * r();
      const set = tirerJeu(r);
      attrs.iCenter.setXYZW(i, rad * Math.cos(a), -1.7 + 3.6 * ((i * 0.618034) % 1), rad * Math.sin(a), set.seed);
      e.set((r() - 0.5) * 1.0, r() * Math.PI * 2, (r() - 0.5) * 1.2);
      q.setFromEuler(e);
      attrs.iOrient.setXYZW(i, q.x, q.y, q.z, q.w);
      attrs.iMisc.setXYZW(i, (0.85 + 0.3 * r()) * WIDTH, (0.6 + 0.9 * r()) * (r() < 0.5 ? -1 : 1), extra ? 1 : 0, set.seed);
      this._sets.push(set);
      this._writeSet(attrs, i, 'A', set);
      this._writeSet(attrs, i, 'B', set);
    }
    for (const k in attrs) geo.setAttribute(k, attrs[k]);
    this._attrs = attrs;
    this._mat = material();
    const U = this._u = this._mat.uniforms;
    U.uMat.value = matcap();
    U.uEps.value = 1 / (this._layout.seg - 1);
    this._mesh = new THREE.InstancedMesh(geo, this._mat, n);
    this._mesh.frustumCulled = false;
    this._group = new THREE.Group();
    this._group.position.set(0, 1.1, -2.5);
    this._group.add(this._mesh);
    scene.add(this._group);
    this._rand = r;

    // l'état musical
    this._time = 0; this._paused = false;
    this._phaseBeat = null; this._phaseBar = null; this._phasePhrase = null; this._free = 0; this._beats = 0; this._bars = 0; this._phrases = 0;
    this._secondsBeat = 0.5; this._pauseClock = 0; this._clock = 0;
    this._sinceBeat = 10; this._sinceBar = 10; this._barPhase = 0;
    this._low = 0.5; this._mid = 0.5; this._high = 0.5; this._ripple = 0;
    this._mat0 = this._forcedMat != null ? this._forcedMat : 0; this._matFrom = this._mat0; this._matTo = this._mat0; this._matAt = -1e9;
    this._setAt = -1; this._nextAction = 0;
    this._dropAt = -1e9; this._dropUntil = -1e9; this._reveal = 0; this._twistGain = 1; this._dropLum = 1;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null; this._lastMode = null;
    this._paletteKey = null;
    this._applyPalette({ artworkPalette: null });
    if (this._probe && typeof window !== 'undefined') window.__ribbons = this;
  }

  _writeSet(attrs, i, which, set) {
    const a0 = attrs['i' + which + '0'], a1 = attrs['i' + which + '1'], a2 = attrs['i' + which + '2'];
    a0.setXYZW(i, set.fx[0], set.fx[1], set.fx[2], set.w[0]);
    a1.setXYZW(i, set.fy[0], set.fy[1], set.fy[2], set.w[1]);
    a2.setXYZW(i, set.fz[0], set.fz[1], set.fz[2], set.w[2]);
  }

  _applyPalette(profile) {
    const pal = profile.artworkPalette;
    const v = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors : [0xff4fa0, 0x3fb6ff, 0xffd23f];
    const key = v.map(String).join('|');
    if (key === this._paletteKey) return;
    this._paletteKey = key;
    const hsl = { h: 0, s: 0, l: 0 };
    const c = new THREE.Color();
    for (let i = 0; i < 12; i++) {
      // chaque ruban : une couleur de la palette (saturée par le preset), un
      // léger décalage de teinte pour que deux rubans de même couleur se
      // distinguent ; le max des canaux ramené à 1 (la matcap porte la luminance)
      vdjColor(v[i % v.length], c, LOUNGE.saturation).getHSL(hsl);
      const h = hsl.h + ((i * 0.37) % 1 - 0.5) * 0.06;
      c.setHSL(((h % 1) + 1) % 1, Math.max(LOUNGE.saturation, hsl.s), 0.5);
      const m = Math.max(c.r, c.g, c.b) || 1;
      this._u.uTints.value[i].setRGB(c.r / m, c.g / m, c.b / m);
      // le dos : une teinte voisine, plus sombre
      c.setHSL((((h + 0.08) % 1) + 1) % 1, Math.max(LOUNGE.saturation, hsl.s), 0.5);
      const m2 = Math.max(c.r, c.g, c.b) || 1;
      this._u.uBacks.value[i].setRGB(0.45 * c.r / m2, 0.45 * c.g / m2, 0.45 * c.b / m2);
    }
  }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) this._dropIdx = 0;
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) {
      if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true;
      this._dropIdx++;
    }
    return fired;
  }

  onTrackChange(profile) { this._applyPalette(profile); this._dropIdx = 0; this._lastNowMs = null; }

  // LA COUPE DU PRESET LOUNGE : une phrase sur deux, alternativement un fondu
  // de matériau (satin <-> chrome) et un nouvel ensemble de fréquences
  _phraseCut() {
    if (this._nextAction === 0 && this._forcedMat == null) {
      this._matFrom = this._matNow(); this._matTo = this._matTo > 0.5 ? 0 : 1; this._matAt = this._time;
    } else if (this._setAt < 0) {
      for (let i = 0; i < this._n; i++) {
        const set = tirerJeu(this._rand);
        this._sets[i] = set;
        this._writeSet(this._attrs, i, 'B', set);
        this._attrs.iMisc.setW(i, set.seed);
      }
      for (const k of ['iB0', 'iB1', 'iB2', 'iMisc']) this._attrs[k].needsUpdate = true;
      this._setAt = this._time;
    }
    this._nextAction = 1 - this._nextAction;
  }
  _matNow() {
    const u = clamp01((this._time - this._matAt) / (4 * this._secondsBeat));
    return this._matFrom + (this._matTo - this._matFrom) * u * u * (3 - 2 * u);
  }

  update(deltaTime, profile) {
    const dt = Math.min(deltaTime, 0.1);
    this._paused = profile.playbackState !== 'PLAYING';
    this._applyPalette(profile);
    const director = profile.director;
    const bands = (director && director.hasReliableGrid && director.bands) ? director.bands : null;
    if (bands) {
      const k = 1 - Math.exp(-dt / 0.4);           // lissées : le lounge n'a pas d'à-coup
      this._low += (clamp01(bands.low) - this._low) * k; this._mid += (clamp01(bands.mid) - this._mid) * k; this._high += (clamp01(bands.high) - this._high) * k;
    }
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    this._secondsBeat = bpm > 0 ? 60 / bpm : 0.5;
    const beatS = this._secondsBeat, barS = 4 * beatS;
    this._time += dt;

    let beat = false, bar = false, phrase = false;
    if (!this._paused) {
      const bp = profile.beatPhase;
      if (bp != null) { if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) beat = true; this._phaseBeat = bp; }
      else { this._free += dt; if (this._free >= beatS) { this._free -= beatS; beat = true; } }
      const barPhase = profile.barPhase;
      if (barPhase != null) { if (this._phaseBar != null && barPhase < this._phaseBar - 0.5) bar = true; this._phaseBar = barPhase; this._barPhase = barPhase; }
      else { if (beat && (this._beats + 1) % 4 === 0) bar = true; this._barPhase = clamp01(this._sinceBar / barS); }
      const ph = profile.phrasePhase;
      if (ph != null) { if (this._phasePhrase != null && ph < this._phasePhrase - 0.5) phrase = true; this._phasePhrase = ph; }
      else if (beat && (this._beats + 1) % 16 === 0) phrase = true;
      if (beat) { this._beats++; this._sinceBeat = 0; }
      if (bar) { this._bars++; this._sinceBar = 0; }
      if (phrase) { this._phrases++; if (this._phrases % 2 === 0) this._phraseCut(); }
      this._clock = this._beats + (bp != null ? bp : this._free / beatS) + this._pauseClock;
    } else {
      // pause : l'ondulation ralentie à 5 %
      this._pauseClock += dt / beatS * PAUSE_RATE;
      this._clock += dt / beatS * PAUSE_RATE;
    }
    this._sinceBeat += dt; this._sinceBar += dt;

    // DROP (lounge) : pas de flash ; +25 % sur une mesure, puis la variante
    // la plus riche 8 mesures (deux rubans de plus qui se déroulent, torsion x1,6)
    let drop = !this._paused && this._tickDrops(profile);
    if (director && director.mode === 'drop' && this._lastMode !== 'drop' && director.hasReliableGrid && this._time > this._dropUntil) drop = true;
    this._lastMode = director ? director.mode : null;
    if (drop) { this._dropAt = this._time; this._dropUntil = this._time + DROP_BARS * barS; }
    const inDrop = this._time < this._dropUntil;
    const rate = dt / (REVEAL_BARS * barS);
    this._reveal = clamp01(this._reveal + (inDrop ? rate : -rate));
    this._twistGain += ((inDrop ? DROP_TWIST : 1) - this._twistGain) * (1 - Math.exp(-dt / barS));
    const rise = inDrop ? clamp01((this._time - this._dropAt) / barS) : clamp01(1 - (this._time - this._dropUntil) / barS);
    this._dropLum = 1 + LOUNGE.dropRise.gain * rise;

    // le temps : une respiration de LARGEUR seulement (+6 %, 80 ms / 400 ms,
    // l'enveloppe du preset). Largeur et luminance ensemble faisaient ~10 %
    // de lumière en plus sur le temps : au-delà des 8 % du preset, et assez
    // pour taper du pied
    const breath = this._paused ? 1 : vdjBeatBreath(this._sinceBeat, LOUNGE);
    const breathEnv = (breath - 1) / LOUNGE.breath.gain;
    // la vague rapide (bands.mid) : un taux intégré, jamais une distance
    this._ripple = (this._ripple + dt / beatS * (this._paused ? PAUSE_RATE : 1) * (0.15 + 0.5 * this._mid)) % (Math.PI * 2);

    const U = this._u, clock = this._clock;
    U.uT.value = vdjLoop(clock, LOOP_BEATS) * Math.PI * 2;
    U.uTwistT.value = vdjLoop(clock, TWIST_BEATS) * Math.PI * 2;
    U.uRipple.value = this._ripple;
    U.uTwistGain.value = this._twistGain;
    U.uReveal.value = this._reveal;
    U.uWidth.value = (0.8 + 0.4 * this._low) * (1 + 0.08 * Math.sin(vdjLoop(clock, BREATH_BEATS) * Math.PI * 2)) * (1 + BEAT_WIDTH * breathEnv);
    // le fondu de jeu de fréquences : une mesure, puis B recopié dans A
    if (this._setAt >= 0) {
      const u = clamp01((this._time - this._setAt) / barS);
      U.uSetMix.value = u * u * (3 - 2 * u);
      if (u >= 1) {
        for (const [a, b] of [['iA0', 'iB0'], ['iA1', 'iB1'], ['iA2', 'iB2']]) { this._attrs[a].array.set(this._attrs[b].array); this._attrs[a].needsUpdate = true; }
        for (let i = 0; i < this._n; i++) this._attrs.iCenter.setW(i, this._attrs.iMisc.getW(i));
        this._attrs.iCenter.needsUpdate = true;
        U.uSetMix.value = 0; this._setAt = -1;
      }
    }
    U.uMatMix.value = this._forcedMat != null ? this._forcedMat : this._matNow();
    // la mesure : le reflet court d'un bout à l'autre
    U.uBarPos.value = -0.1 + 1.2 * this._barPhase;
    U.uStreak.value = this._paused || this._noStreak ? 0 : 0.4 + 0.6 * this._high;
    // la caméra : une révolution lente, en 64 temps, sans à-coup (le groupe tourne)
    this._group.rotation.y = vdjLoop(clock, ORBIT_BEATS) * Math.PI * 2;
    this._group.rotation.x = 0.10 * Math.sin(vdjLoop(clock, ORBIT_BEATS) * Math.PI * 4);
    // le confort : aucun flash ; luminance moyenne (estimée) sous 0,35
    const lum = this._dropLum;
    const estimate = 0.035 * (this._count + EXTRA * this._reveal) / 8 * U.uWidth.value * lum;
    U.uLum.value = lum * this._comfort.measure(estimate, dt);
    // la séparation RGB : sur la mesure seulement, x1,5, 300 ms
    this._post.update(this._paused ? 1 : vdjBarChroma(this._sinceBar));
  }

  getProbe() {
    const U = this._u;
    return { tier: this._tier, count: this._count, n: this._n, matMix: U.uMatMix.value, setMix: U.uSetMix.value, reveal: this._reveal, twistGain: this._twistGain, lum: U.uLum.value, barPos: U.uBarPos.value, phrases: this._phrases, beats: this._beats, bars: this._bars, inDrop: this._time < this._dropUntil, aberration: this._post.value.aberration, comfort: this._comfort.average };
  }

  dispose() {
    if (this._scene) this._scene.remove(this._group);
    if (this._geo) this._geo.dispose();            // the clone's attributes only; the cached strip stays
    if (this._mesh) this._mesh.dispose();
    if (typeof window !== 'undefined' && window.__ribbons === this) window.__ribbons = null;
    this._scene = null;
  }
}
